import { MAPBOX_TOKEN } from "../config/mapbox";

export type DirectionsProfile =
  | "mapbox/driving"
  | "mapbox/driving-traffic"
  | "mapbox/walking"
  | "mapbox/cycling";

export interface RoutePoint {
  longitude: number;
  latitude: number;
}

export interface RouteLeg {
  distanceMeters: number;
  durationSeconds: number;
}

export interface RouteResult {
  geometry: GeoJSON.LineString;
  distanceMeters: number;
  durationSeconds: number;
  legs: RouteLeg[];
}

const DIRECTIONS_BASE = "https://api.mapbox.com/directions/v5";

export function profileForCommuteMode(
  mode: string
): DirectionsProfile {
  switch (mode) {
    case "walk":
      return "mapbox/walking";
    case "cycle":
      return "mapbox/cycling";
    case "tube":
    case "bus":
    case "mixed":
      return "mapbox/driving-traffic";
    default:
      return "mapbox/driving";
  }
}

export async function fetchRoute(
  from: RoutePoint,
  to: RoutePoint,
  profile: DirectionsProfile = "mapbox/driving",
  options?: { token?: string }
): Promise<RouteResult | null> {
  const token = options?.token ?? MAPBOX_TOKEN;
  const coords = `${from.longitude},${from.latitude};${to.longitude},${to.latitude}`;
  const params = new URLSearchParams({
    geometries: "geojson",
    overview: "full",
    access_token: token,
  });

  const res = await fetch(
    `${DIRECTIONS_BASE}/${profile}/${coords}?${params}`
  );
  if (!res.ok) throw new Error(`Mapbox directions failed: ${res.status}`);
  const data = await res.json();
  const route = data.routes?.[0];
  if (!route?.geometry) return null;

  const legs: RouteLeg[] = (route.legs ?? []).map(
    (leg: { distance?: number; duration?: number }) => ({
      distanceMeters: leg.distance ?? 0,
      durationSeconds: leg.duration ?? 0,
    })
  );

  return {
    geometry: route.geometry,
    distanceMeters: route.distance ?? 0,
    durationSeconds: route.duration ?? 0,
    legs,
  };
}
